/**
 * Pure helpers behind "Save as view" (design 20a/20b): the name suggested from
 * the filters, the "what gets saved" summaries, and the layout slices that the
 * popover's switches bind to a view. Kept free of React and i18n so they can be
 * unit tested; callers pass in the words.
 */
import type { TaskFilter } from '@/lib/types/filter'
import type {
  ProjectViewPreferences,
  SortBy,
  ViewType,
} from '@/store/ProjectViewPreferencesStore'
import { groupNumbers, isGrouped } from '@/lib/filters/filterGroups'
import {
  parseSavedFilterConfig,
  parseSavedLayoutConfig,
  serializeSavedFilterConfig,
  serializeSavedLayoutConfig,
} from '@/lib/types/savedFilter'

/** Longest name the saved view schema accepts. */
export const VIEW_NAME_MAX = 100
export const VIEW_DESCRIPTION_MAX = 500

const NEGATED = new Set(['excludes', 'not_equal', 'does_not_contain'])

/** A filter's words for the name, e.g. "Overdue" or "not Done". */
export type FilterPhrase = (filter: TaskFilter) => string

interface SuggestWords {
  /** Joins OR groups: "or". */
  or: string
  /** Prefix for negated filters: "not". */
  not: string
}

function filterKey(f: TaskFilter): string {
  return JSON.stringify([f.fieldKey, f.operator, [...f.values].map(String).sort()])
}

function phraseOf(f: TaskFilter, phrase: FilterPhrase, words: SuggestWords): string {
  const text = phrase(f).trim()
  if (!text) return ''
  return NEGATED.has(f.operator) ? `${words.not} ${text}` : text
}

function capitalize(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

function clampName(s: string): string {
  if (s.length <= VIEW_NAME_MAX) return s
  return `${s.slice(0, VIEW_NAME_MAX - 1).trimEnd()}…`
}

/**
 * A name read off the active filters: what every group shares becomes the
 * prefix, the rest of each group is joined by "or".
 *
 *   (My Team, Overdue) or (My Team, High, Test Label)
 *     → "My Team: Overdue or High, Test Label"
 *
 * Returns '' when no filter has a value, so the caller can fall back.
 */
export function suggestViewName(
  filters: ReadonlyArray<TaskFilter>,
  phrase: FilterPhrase,
  words: SuggestWords,
): string {
  const active = filters.filter((f) => f.values.length > 0)
  if (active.length === 0) return ''

  const say = (f: TaskFilter) => phraseOf(f, phrase, words)
  const join = (fs: ReadonlyArray<TaskFilter>) =>
    fs.map(say).filter(Boolean).join(', ')

  if (!isGrouped(active)) return clampName(capitalize(join(active)))

  const everywhere = active.filter((f) => f.group === undefined)
  const groups = groupNumbers(active).map((g) => active.filter((f) => f.group === g))

  // A condition every group repeats reads as part of the prefix, not per group.
  const [first, ...others] = groups
  const shared = (first ?? []).filter((f) =>
    others.every((g) => g.some((o) => filterKey(o) === filterKey(f))),
  )
  const sharedKeys = new Set(shared.map(filterKey))

  const prefix = join([...everywhere, ...shared])
  const alternatives = groups
    .map((g) => join(g.filter((f) => !sharedKeys.has(filterKey(f)))))
    .filter(Boolean)
    .join(` ${words.or} `)

  if (prefix && alternatives) return clampName(capitalize(`${prefix}: ${alternatives}`))
  return clampName(capitalize(prefix || alternatives))
}

/** "2 groups · 5 conditions": OR groups (0 when ungrouped) and set filters. */
export function countConditions(filters: ReadonlyArray<TaskFilter>): {
  groups: number
  conditions: number
} {
  const active = filters.filter((f) => f.values.length > 0)
  return {
    groups: isGrouped(active) ? groupNumbers(active).length : 0,
    conditions: active.length,
  }
}

// ─── Layout slices ───────────────────────────────────────────────────────────

/** Preferences behind "Layout, grouping, sort". */
export const LAYOUT_KEYS = [
  'viewType',
  'boardGroupBy',
  'listGroupBy',
  'boardSwimlanes',
  'ganttScale',
  'detailsPanel',
  'sortBy',
  'sortDirection',
] as const satisfies ReadonlyArray<keyof ProjectViewPreferences>

/** Preferences behind "Visible columns". */
export const COLUMN_KEYS = [
  'density',
  'visibleCustomFieldColumns',
] as const satisfies ReadonlyArray<keyof ProjectViewPreferences>

export interface SaveParts {
  layout: boolean
  columns: boolean
}

function pick(
  prefs: Partial<ProjectViewPreferences>,
  keys: ReadonlyArray<keyof ProjectViewPreferences>,
): Partial<ProjectViewPreferences> {
  const out: Partial<ProjectViewPreferences> = {}
  for (const k of keys) {
    if (prefs[k] !== undefined) Object.assign(out, { [k]: prefs[k] })
  }
  return out
}

/**
 * The layoutConfig to store for the chosen switches, or null when neither is
 * on (a filters-only view, like the ones saved before layouts existed).
 */
export function buildLayoutConfig(
  prefs: Partial<ProjectViewPreferences> | null | undefined,
  parts: SaveParts,
): string | null {
  if (!prefs || (!parts.layout && !parts.columns)) return null
  const slice = {
    ...(parts.layout ? pick(prefs, LAYOUT_KEYS) : {}),
    ...(parts.columns ? pick(prefs, COLUMN_KEYS) : {}),
  }
  return Object.keys(slice).length > 0 ? serializeSavedLayoutConfig(slice) : null
}

/** Summary pieces for "Layout, grouping, sort", as i18n key suffixes. */
export function layoutSummaryKeys(prefs: Partial<ProjectViewPreferences>): {
  viewType: ViewType
  groupBy: string
  sortBy: SortBy
} {
  const viewType = prefs.viewType ?? 'list'
  const groupBy =
    viewType === 'board'
      ? (prefs.boardGroupBy ?? 'status')
      : viewType === 'list'
        ? (prefs.listGroupBy ?? 'status')
        : 'none'
  return { viewType, groupBy, sortBy: prefs.sortBy ?? 'manual' }
}

/**
 * Names for "Visible columns": the custom field columns in order, resolved by
 * id. Unknown ids (a deleted field) are skipped.
 */
export function columnNames(
  prefs: Partial<ProjectViewPreferences>,
  fieldNames: ReadonlyMap<string, string>,
): Array<string> {
  return (prefs.visibleCustomFieldColumns ?? []).flatMap((id) => {
    const name = fieldNames.get(id)
    return name ? [name] : []
  })
}

// ─── Update / reset (design 20b) ─────────────────────────────────────────────

interface ViewConfig {
  filterConfig: string
  layoutConfig: string | null
}

/**
 * The patch for "Update <name>": the current filters, and the current values of
 * whichever layout slices the view already binds. A view saved without layout
 * stays without layout.
 */
export function buildUpdatePatch(
  view: ViewConfig,
  filters: ReadonlyArray<TaskFilter>,
  prefs: Partial<ProjectViewPreferences> | null | undefined,
): ViewConfig {
  const saved = parseSavedLayoutConfig(view.layoutConfig)
  const layoutConfig = saved && prefs
    ? serializeSavedLayoutConfig({ ...saved, ...pick(prefs, Object.keys(saved) as Array<keyof ProjectViewPreferences>) })
    : view.layoutConfig
  return {
    filterConfig: serializeSavedFilterConfig([...filters]),
    layoutConfig,
  }
}

/** The saved state to put back on "Reset to saved". */
export function resetTargets(view: ViewConfig): {
  filters: Array<TaskFilter>
  layout: Partial<ProjectViewPreferences> | null
} {
  return {
    filters: parseSavedFilterConfig(view.filterConfig),
    layout: parseSavedLayoutConfig(view.layoutConfig),
  }
}

/** Snapshot of a view's config, for Undo after an update. */
export function snapshotConfig(view: ViewConfig): ViewConfig {
  return { filterConfig: view.filterConfig, layoutConfig: view.layoutConfig }
}

/** What an empty name falls back to on submit. */
export function resolveViewName(typed: string, suggested: string, untitled: string): string {
  const name = typed.trim() || suggested.trim() || untitled
  return clampName(name)
}
